import { Helmet } from "react-helmet-async";
import { useSiteConfig } from "@/hooks/useSiteConfig";
import { mediaUrl } from "@/lib/api";
import { COMPANY } from "@/lib/policies";
import { BRAND_NAME, BRAND_HANDLE, personJsonLd, orgJsonLd, websiteJsonLd, softwareAppJsonLd } from "@/lib/brand";

function verificationContent(raw) {
  let s = String(raw || "").trim().replace(/^["']|["']$/g, "");
  s = s.replace(/^google-site-verification\s*[=:]\s*/i, "");
  s = s.replace(/^content\s*=\s*/i, "").replace(/^["']|["']$/g, "");
  return s;
}

const DEFAULTS = {
  title: "Mera Startup — India's Best Shopify Alternative",
  description:
    "India's best Shopify alternative. Launch a full ecommerce website — cart, UPI, COD, GST billing, SEO and your own Android app.",
  keywords:
    "merastartup, mera startup, ecommerce website india, online store builder, sell online india, shopify alternative india, whatsapp checkout, cash on delivery store, android app for shop",
};

/**
 * Site-wide SEO + analytics/pixel injector for the public marketing site.
 * Reads seo + marketing config from /api/landing (super-admin managed) and
 * injects meta tags, Open Graph/Twitter cards, JSON-LD, and
 * GA4 / GTM / Meta Pixel / Google Ads scripts when their IDs are set.
 */
export default function SiteHead({
  title,
  description,
  path = "",
  image,
  type = "website",
  noIndex = false,
  faq,
  extraJsonLd,
  published,
  modified,
  author,
  keywords: keywordsOverride,
}) {
  const cfg = useSiteConfig();
  const brand = cfg.brand || {};
  const seo = cfg.seo || {};
  const mkt = cfg.marketing || {};
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const url = origin + (path || "");

  const finalTitle = title || seo.meta_title || brand.site_title || DEFAULTS.title;
  const finalDesc = description || seo.meta_description || brand.tagline || DEFAULTS.description;
  const keywordsRaw = keywordsOverride || seo.meta_keywords || DEFAULTS.keywords;
  const keywords = /merastartup/i.test(keywordsRaw || "") ? keywordsRaw : `merastartup, ${keywordsRaw || DEFAULTS.keywords}`;
  const favicon = brand.favicon_url ? mediaUrl(brand.favicon_url) : null;
  const ogImage =
    image ||
    (seo.og_image ? mediaUrl(seo.og_image) : null) ||
    (brand.logo_url ? mediaUrl(brand.logo_url) : null);
  const robots = seo.robots_index === false || noIndex ? "noindex, nofollow" : "index, follow";

  const ga4 = (seo.ga4_id || "").trim();
  const gtm = (seo.gtm_id || "").trim();
  const pixel = (mkt.fb_pixel_id || "").trim();
  const gads = (mkt.google_ads_id || "").trim();

  const locale = seo.default_language || "en-IN";
  const faqItems = (faq && faq.length ? faq : seo.faq) || [];
  const sameAs = (seo.same_as || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  const siteName = brand.site_title || BRAND_NAME;
  const orgLd = orgJsonLd(origin, ogImage, sameAs);
  if (COMPANY.email) orgLd.email = COMPANY.email;
  if (COMPANY.phone) orgLd.telephone = COMPANY.phone;
  const websiteLd = { ...websiteJsonLd(origin), inLanguage: locale };

  const appLd = !path || path === "/" || path === "/pricing" || path === "/features" ? softwareAppJsonLd(origin) : null;

  const faqLd = faqItems.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqItems.filter((x) => x?.q && x?.a).map((x) => ({
          "@type": "Question",
          name: x.q,
          acceptedAnswer: { "@type": "Answer", text: x.a },
        })),
      }
    : null;

  const crumbs = [{ "@type": "ListItem", position: 1, name: "Home", item: origin || undefined }];
  if (path && path !== "/") {
    const parts = path.split("/").filter(Boolean);
    parts.forEach((part, i) => {
      const href = `${origin}/${parts.slice(0, i + 1).join("/")}`;
      crumbs.push({
        "@type": "ListItem",
        position: i + 2,
        name: decodeURIComponent(part).replace(/-/g, " "),
        item: href,
      });
    });
  }
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs,
  };

  return (
    <Helmet>
      <title>{finalTitle}</title>
      {favicon ? <link rel="icon" href={favicon} /> : null}
      <meta name="description" content={finalDesc} />
      {keywords ? <meta name="keywords" content={keywords} /> : null}
      <meta name="robots" content={robots} />
      <html lang={locale} />
      {url ? <link rel="canonical" href={url} /> : null}
      {url ? <link rel="alternate" hrefLang="en-IN" href={url} /> : null}
      {url ? <link rel="alternate" hrefLang="x-default" href={url} /> : null}
      <link rel="alternate" type="application/rss+xml" title={`${siteName} Blog`} href={`${origin}/rss.xml`} />
      {seo.geo_placename ? <meta name="geo.placename" content={seo.geo_placename} /> : null}
      {seo.geo_region ? <meta name="geo.region" content={seo.geo_region} /> : null}

      {/* Open Graph */}
      <meta property="og:site_name" content={siteName} />
      <meta property="og:type" content={type} />
      <meta property="og:locale" content={(locale || "en-IN").replace("-", "_")} />
      <meta property="og:title" content={seo.og_title || finalTitle} />
      <meta property="og:description" content={finalDesc} />
      {url ? <meta property="og:url" content={url} /> : null}
      {ogImage ? <meta property="og:image" content={ogImage} /> : null}

      {/* Twitter */}
      <meta name="twitter:card" content={ogImage ? "summary_large_image" : "summary"} />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDesc} />
      {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}
      {seo.twitter_handle ? <meta name="twitter:site" content={seo.twitter_handle} /> : null}

      {/* Search engine verification */}
      {verificationContent(seo.google_site_verification) ? (
        <meta name="google-site-verification" content={verificationContent(seo.google_site_verification)} />
      ) : null}
      {seo.bing_site_verification ? (
        <meta name="msvalidate.01" content={seo.bing_site_verification} />
      ) : null}

      {published ? <meta property="article:published_time" content={published} /> : null}
      {modified ? <meta property="article:modified_time" content={modified} /> : null}
      {author ? <meta property="article:author" content={author} /> : null}

      {/* Structured data */}
      <script type="application/ld+json">{JSON.stringify(orgLd)}</script>
      <script type="application/ld+json">{JSON.stringify(personJsonLd(origin))}</script>
      <script type="application/ld+json">{JSON.stringify(websiteLd)}</script>
      {appLd ? <script type="application/ld+json">{JSON.stringify(appLd)}</script> : null}
      <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
      {faqLd ? <script type="application/ld+json">{JSON.stringify(faqLd)}</script> : null}
      {extraJsonLd ? <script type="application/ld+json">{JSON.stringify(extraJsonLd)}</script> : null}

      {/* Google Tag Manager */}
      {gtm ? (
        <script>{`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${gtm}');`}</script>
      ) : null}

      {/* Google Analytics 4 + Google Ads (gtag.js) */}
      {ga4 || gads ? (
        <script async src={`https://www.googletagmanager.com/gtag/js?id=${ga4 || gads}`}></script>
      ) : null}
      {ga4 || gads ? (
        <script>{`window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());${ga4 ? `gtag('config','${ga4}');` : ""}${gads ? `gtag('config','${gads}');` : ""}`}</script>
      ) : null}

      {/* Meta (Facebook) Pixel */}
      {pixel ? (
        <script>{`!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${pixel}');fbq('track','PageView');`}</script>
      ) : null}
    </Helmet>
  );
}
